// IconPicker.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useIcons } from 'context/IconsContext';
import { useTheme } from 'context/ThemeContext';

// Hooks
import usePreloadIcons from 'hooks/usePreloadIcons';

// CSS
import './IconPicker.css';

// Icons
import Icon from 'svg/Icon';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * IconPicker
 *
 * Displays a searchable, scrollable grid of icons backed by the in-memory icons cache.
 *
 * @param {number|string} width - CSS width for the picker surface.
 * @param {number|string} height - CSS height for the picker surface.
 * @param {string|null} selectedIconKey - Optional icon key to highlight.
 * @param {function} onSelect - Callback invoked with the selected icon key.
 * @param {function} onClear - Optional callback invoked to clear the selection.
 *
 * @returns {ReactNode} - IconPicker.
 */
const IconPicker = React.memo(({
  width = 400,
  height = 400,
  selectedIconKey = null,
  onSelect,
  onClear,
}) => {

  // ----- CONTEXT ----- //

  // Icons Context

  const { iconsByKey } = useIcons();

  // Theme Context
  const { theme } = useTheme();

  // ----- STATE ----- //

  // Clear Button Hover State

  const [clearHovered, setClearHovered] = useState(false);

  // Hovered Icon Key State
  const [hoveredIconKey, setHoveredIconKey] = useState('');

  // Search Query State
  const [query, setQuery] = useState('');

  // ----- MEMOS ----- //

  // Sorted icon list from the in-memory cache.

  const iconList = useMemo(() => {
    if (!iconsByKey) return [];
    return Object.values(iconsByKey)
      .filter(Boolean)
      .sort((a, b) => String(a?.key || '').localeCompare(String(b?.key || '')));
  }, [iconsByKey]);

  // Filtered icon list based on the normalized search query.
  const filteredIcons = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return iconList;

    return iconList.filter((icon) => {
      const index = Array.isArray(icon?.index) ? icon.index : [];
      return index.some((entry) => String(entry).toLowerCase().includes(normalizedQuery));
    });
  }, [iconList, query]);

  // ----- UTILITIES ----- //

  // Converts numeric dimensions to px values and passes through string values.

  const toCssValue = (value) => (
    typeof value === 'number' ? `${value}px` : value
  );

  // Preloads icon data used by the picker.
  usePreloadIcons();

  // ----- HANDLERS ----- //

  /**
   * Selects an icon key and passes it to the parent callback.
   *
   * @param {string} key - The selected icon key.
   */

  const handleSelect = (key) => {
    if (typeof onSelect === 'function') onSelect(key);
  };

  // ----- RENDER ----- //

  return (
    <div
      className="icon-picker-container"
      style={{
        height: toCssValue(height),
        width: toCssValue(width),
      }}
    >

      {/* HEADER */}
      <div className="icon-picker-header">

        {/* HEADER ROW */}
        <div className="icon-picker-header-row">

          <TextInput
            placeholder="Search icons"
            startIconKey="search"
            value={query}
            width="100%"
            onChange={(e) => setQuery(e.target.value)}
          />

          {/* CLEAR */}
          {typeof onClear === 'function' && (

            <button
              className="icon-picker-clear"
              style={{
                backgroundColor: clearHovered ? theme.backgroundHover : theme.panel,
              }}
              type="button"
              onClick={() => onClear()}
              onMouseEnter={() => setClearHovered(true)}
              onMouseLeave={() => setClearHovered(false)}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* BODY */}
      <div className="icon-picker-body">

        {/* EMPTY */}
        {!iconsByKey && (
          <div
            className="icon-picker-empty"
          >
            Loading icons...
          </div>
        )}

        {/* EMPTY */}
        {iconsByKey && filteredIcons.length === 0 && (
          <div
            className="icon-picker-empty"
          >
            No icons match that search.
          </div>
        )}

        {iconsByKey && filteredIcons.length > 0 && (
          <div className="icon-picker-grid">
            {filteredIcons.map((icon) => (
              <button
                className="icon-picker-item"
                style={{
                  backgroundColor:
                    icon.key === selectedIconKey
                      ? theme.input
                      : (hoveredIconKey === icon.key ? theme.backgroundHover : 'transparent'),
                  borderColor:
                    icon.key === selectedIconKey
                      ? theme.foregroundMuted
                      : (hoveredIconKey === icon.key ? theme.foregroundMuted : 'transparent'),
                }}
                key={icon.key}
                data-selected={icon.key === selectedIconKey}
                type="button"
                title={icon.key}
                onClick={() => handleSelect(icon.key)}
                onMouseEnter={() => setHoveredIconKey(icon.key)}
                onMouseLeave={() => setHoveredIconKey('')}
              >
                <Icon
                  iconKey={icon.key}
                  width={22}
                  height={22}
                  color={theme.foreground}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// Dev Tools
IconPicker.displayName = 'IconPicker';

export default IconPicker;
